/**
 * Error codes the backend returns in `ApiErrorBody.code` (and sometimes in
 * `message`). Migrated 1:1 from legacy `shared/consts/error-codes.ts`.
 *
 * A code is only turned into a user-facing message when it is listed here —
 * that is what keeps an unknown backend code from leaking a raw identifier
 * into the UI. Each one has a `serverError.{CODE}` translation key.
 */
export const ERROR_CODES = {
  ACCESS_DENIED: 'ACCESS_DENIED',
  ADDRESS_CONFLICTING_CONTRACT: 'ADDRESS_CONFLICTING_CONTRACT',
  ADDRESS_CONFLICTING_ORDER: 'ADDRESS_CONFLICTING_ORDER',
  BAD_REQUEST: 'BAD_REQUEST',
  CLIENT_EMAIL_MISSING: 'CLIENT_EMAIL_MISSING',
  COMMUNICATION_ERROR: 'COMMUNICATION_ERROR',
  CONFLICT: 'CONFLICT',
  DOCUMENT_DEFINITION_IN_USE: 'DOCUMENT_DEFINITION_IN_USE',
  DOCUMENT_DEFINITION_INVALID_ASSIGNMENT: 'DOCUMENT_DEFINITION_INVALID_ASSIGNMENT',
  DOCUMENT_DEFINITION_INVALID_NAME: 'DOCUMENT_DEFINITION_INVALID_NAME',
  DOCUMENT_DEFINITION_NAME_TAKEN: 'DOCUMENT_DEFINITION_NAME_TAKEN',
  DOCUMENT_DEFINITION_NOT_FOUND: 'DOCUMENT_DEFINITION_NOT_FOUND',
  DOCUMENT_DEFINITION_PHASE_REQUIRED: 'DOCUMENT_DEFINITION_PHASE_REQUIRED',
  DOCUMENT_DEFINITION_USED_BY_OFFERS: 'DOCUMENT_DEFINITION_USED_BY_OFFERS',
  DOCUMENT_TEMPLATE_BRAND_NOT_ALLOWED: 'DOCUMENT_TEMPLATE_BRAND_NOT_ALLOWED',
  DOCUMENT_TEMPLATE_INCOMPLETE_BRANDS: 'DOCUMENT_TEMPLATE_INCOMPLETE_BRANDS',
  DOCUMENT_TEMPLATE_INVALID_FILE_TYPE: 'DOCUMENT_TEMPLATE_INVALID_FILE_TYPE',
  DOCUMENT_TEMPLATE_NO_ACTIVE_FOR_TYPE: 'DOCUMENT_TEMPLATE_NO_ACTIVE_FOR_TYPE',
  DOCUMENT_TEMPLATE_NOT_DRAFT: 'DOCUMENT_TEMPLATE_NOT_DRAFT',
  DOCUMENT_TEMPLATE_NOT_FOUND: 'DOCUMENT_TEMPLATE_NOT_FOUND',
  EMAIL_FAILED: 'EMAIL_FAILED',
  EMPTY_RESPONSE: 'EMPTY_RESPONSE',
  FEE_DEFINITION_INACTIVE: 'FEE_DEFINITION_INACTIVE',
  FEE_DEFINITION_NOT_FOUND: 'FEE_DEFINITION_NOT_FOUND',
  FEE_DEFINITION_SERVICE_TYPE_MISMATCH: 'FEE_DEFINITION_SERVICE_TYPE_MISMATCH',
  INACTIVE_ACCOUNT: 'INACTIVE_ACCOUNT',
  INVALID_ACTUAL_PASSWORD: 'INVALID_ACTUAL_PASSWORD',
  INVALID_CAPTCHA: 'INVALID_CAPTCHA',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  INVALID_FORM: 'INVALID_FORM',
  INVALID_LOGIN: 'INVALID_LOGIN',
  INVALID_RESET_LINK: 'INVALID_RESET_LINK',
  INVALID_TOKEN: 'INVALID_TOKEN',
  INVALID_USER_OR_PASSWORD: 'INVALID_USER_OR_PASSWORD',
  MAIN_SERVICE_FEE_AMOUNT_NOT_POSITIVE: 'MAIN_SERVICE_FEE_AMOUNT_NOT_POSITIVE',
  MAIN_SERVICE_FEE_DUPLICATE: 'MAIN_SERVICE_FEE_DUPLICATE',
  MAIN_SERVICE_FEE_EMPTY: 'MAIN_SERVICE_FEE_EMPTY',
  MAIN_SERVICE_FEE_NOT_FOUND: 'MAIN_SERVICE_FEE_NOT_FOUND',
  MAIN_SERVICE_FEE_REQUIRED: 'MAIN_SERVICE_FEE_REQUIRED',
  MAIN_SERVICE_FEE_SORT_MAPPING_MISSING: 'MAIN_SERVICE_FEE_SORT_MAPPING_MISSING',
  MAIN_SERVICE_FEE_WRONG_API_FOR_PACKAGE: 'MAIN_SERVICE_FEE_WRONG_API_FOR_PACKAGE',
  MAIN_SERVICE_FEE_WRONG_API_FOR_SINGLE: 'MAIN_SERVICE_FEE_WRONG_API_FOR_SINGLE',
  MFA_ACTIVATION_REQUIRED: 'MFA_ACTIVATION_REQUIRED',
  NO_PAPER_INVOICES_FOUND: 'NO_PAPER_INVOICES_FOUND',
  NOT_FOUND: 'NOT_FOUND',
  NOT_FOUND_ERROR: 'NOT_FOUND_ERROR',
  OFFER_NO_ROOM_FOR_CONSENT_SURCHARGE: 'OFFER_NO_ROOM_FOR_CONSENT_SURCHARGE',
  OFFER_PRICE_OVER_STANDARD: 'OFFER_PRICE_OVER_STANDARD',
  OFFER_TERMS_DEFINITION_REQUIRED: 'OFFER_TERMS_DEFINITION_REQUIRED',
  OFFER_TERMS_NOT_ALLOWED: 'OFFER_TERMS_NOT_ALLOWED',
  /*
   * Oba są zabezpieczeniem, nie normalną ścieżką: przy działającym `canCreateVariants`
   * ikona klonowania jest wygaszona wszędzie tam, gdzie któryś by wyleciał.
   * Zobaczy je ten, kto strzeli w API wprost, albo ten, komu ktoś inny sklonuje
   * ten sam cennik w międzyczasie.
   */
  OFFER_VARIANT_ALL_EXIST: 'OFFER_VARIANT_ALL_EXIST',
  OFFER_VARIANT_SOURCE_NOT_BASE: 'OFFER_VARIANT_SOURCE_NOT_BASE',
  ORDER_OFFER_NOT_AVAILABLE: 'ORDER_OFFER_NOT_AVAILABLE',
  ORDER_ONE_PLN_PERIODS_OUT_OF_RANGE: 'ORDER_ONE_PLN_PERIODS_OUT_OF_RANGE',
  PERMISSION_EXCEPTION: 'PERMISSION_EXCEPTION',
  PRODUCT_PRICE_OVER_STANDARD: 'PRODUCT_PRICE_OVER_STANDARD',
  PRODUCT_STANDARD_PRICE_NOT_FOUND: 'PRODUCT_STANDARD_PRICE_NOT_FOUND',
  REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
  RESET_LINK_EXPIRED: 'RESET_LINK_EXPIRED',
  RESET_LINK_USED: 'RESET_LINK_USED',
  SELLER_NOT_FOUND: 'SELLER_NOT_FOUND',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  USER_EXISTS: 'USER_EXISTS',
  USER_LOCKED: 'USER_LOCKED',
  USER_LOCKED_REASON: 'USER_LOCKED_REASON',
  WEB_SECURITY_EXCEPTION: 'WEB_SECURITY_EXCEPTION',
} as const;

type ErrorCode = keyof typeof ERROR_CODES;

/** Translation-key prefix every server error message lives under. */
export const SERVER_ERROR_PREFIX = 'serverError.';

export function isKnownErrorCode(value: string | undefined | null): value is ErrorCode {
  return !!value && value in ERROR_CODES;
}
